import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AlertTriangle, Check, Copy, Loader2, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { useHydrated } from "@/hooks/use-hydrated";
import { Navbar } from "@/components/Navbar";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { pageHead } from "@/lib/seo";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/errors")({
  head: () =>
    pageHead({
      path: "/admin/errors",
      title: "Error log — Book Swap admin",
      description: "Admin-only log of app failures with cause, impact and status.",
      noindex: true,
    }),
  component: AdminErrorsPage,
});

type ErrorEvent = {
  id: string;
  fingerprint: string;
  summary: string;
  details: string | null;
  path: string | null;
  source: string;
  severity: string;
  likely_cause: string | null;
  suggested_fix: string | null;
  occurrences: number;
  affected_users: string[] | null;
  last_user_email: string | null;
  last_user_agent: string | null;
  first_seen_at: string;
  last_seen_at: string;
  resolved_at: string | null;
};

const SEVERITY_STYLES: Record<string, string> = {
  critical: "bg-destructive/10 text-destructive",
  error: "bg-primary-soft text-primary",
  warning: "bg-muted text-muted-foreground",
};

function AdminErrorsPage() {
  const { user, loading } = useAuth();
  const hydrated = useHydrated();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [rows, setRows] = useState<ErrorEvent[]>([]);
  const [busy, setBusy] = useState(true);
  const [showResolved, setShowResolved] = useState(false);

  useEffect(() => {
    if (!hydrated || loading) return;
    if (!user) {
      setIsAdmin(false);
      setBusy(false);
      return;
    }
    let active = true;
    (async () => {
      const { data } = await supabase.rpc("has_role" as never, {
        _user_id: user.id,
        _role: "admin",
      } as never);
      if (!active) return;
      setIsAdmin(data === true);
      if (data !== true) setBusy(false);
    })();
    return () => {
      active = false;
    };
  }, [hydrated, loading, user]);

  const load = async () => {
    setBusy(true);
    const { data, error } = await supabase
      .from("error_events" as never)
      .select("*")
      .order("last_seen_at", { ascending: false })
      .limit(200);
    if (error) toast.error("Couldn't load the error log.");
    setRows((data ?? []) as unknown as ErrorEvent[]);
    setBusy(false);
  };

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin]);

  const toggleResolved = async (row: ErrorEvent) => {
    const next = row.resolved_at ? null : new Date().toISOString();
    const { error } = await supabase
      .from("error_events" as never)
      .update({ resolved_at: next } as never)
      .eq("id", row.id);
    if (error) return toast.error("Couldn't update that issue.");
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, resolved_at: next } : r)));
  };

  const copyReport = async (row: ErrorEvent) => {
    const text = [
      `Fix this Book Swap error:`,
      `Error: ${row.summary}`,
      `Page: ${row.path ?? "unknown"} (${row.source})`,
      `Severity: ${row.severity} · seen ${row.occurrences}x · ${row.affected_users?.length ?? 0} user(s) affected`,
      `First seen: ${row.first_seen_at} · Last seen: ${row.last_seen_at}`,
      row.likely_cause ? `Likely cause: ${row.likely_cause}` : "",
      row.suggested_fix ? `Suggested fix: ${row.suggested_fix}` : "",
      row.details ? `Technical details: ${row.details}` : "",
    ]
      .filter(Boolean)
      .join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Report copied — paste it to Lovable to get it fixed.");
    } catch {
      toast.error("Copy failed. Select the text manually.");
    }
  };

  const visible = rows.filter((r) => (showResolved ? true : !r.resolved_at));

  if (!hydrated || loading || isAdmin === null) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="mx-auto max-w-md px-4 py-24 text-center">
          <h1 className="text-2xl font-bold">Admins only</h1>
          <p className="mt-2 text-muted-foreground">
            This page shows sensitive failure data, so it's restricted to app admins.
          </p>
          <Link to="/" className="mt-6 inline-block">
            <Button>Go home</Button>
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 py-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-3xl font-bold">
              <AlertTriangle className="h-7 w-7 text-primary" />
              Error log
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Every failure the app has reported, grouped and explained. Copy one and send it
              to Lovable to get it fixed.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowResolved((v) => !v)}>
              {showResolved ? "Hide resolved" : "Show resolved"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => void load()} disabled={busy}>
              <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {busy && rows.length === 0 ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : visible.length === 0 ? (
          <div className="mt-12 rounded-xl border border-border bg-card p-10 text-center">
            <p className="text-lg font-medium">No errors reported 🎉</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Failures show up here automatically the moment they happen.
            </p>
          </div>
        ) : (
          <ul className="mt-6 space-y-4">
            {visible.map((row) => (
              <li
                key={row.id}
                className="rounded-xl border border-border bg-card p-5 transition-shadow hover:shadow-md"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${
                      SEVERITY_STYLES[row.severity] ?? SEVERITY_STYLES.error
                    }`}
                  >
                    {row.severity}
                  </span>
                  <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
                    {row.occurrences}× · {row.affected_users?.length ?? 0} user(s)
                  </span>
                  {row.resolved_at ? (
                    <span className="rounded-full bg-primary-soft px-2.5 py-0.5 text-xs text-primary">
                      Resolved
                    </span>
                  ) : null}
                  <span className="ml-auto text-xs text-muted-foreground">
                    {new Date(row.last_seen_at).toLocaleString()}
                  </span>
                </div>

                <p className="mt-3 font-medium break-words">{row.summary}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {row.source}
                  {row.path ? ` — ${row.path}` : ""}
                </p>

                {row.likely_cause ? (
                  <p className="mt-3 text-sm">
                    <span className="font-semibold">Likely cause: </span>
                    {row.likely_cause}
                  </p>
                ) : null}
                {row.suggested_fix ? (
                  <p className="mt-1 text-sm">
                    <span className="font-semibold">What to do: </span>
                    {row.suggested_fix}
                  </p>
                ) : null}

                {row.details ? (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-sm text-muted-foreground">
                      Technical details
                    </summary>
                    <pre className="mt-2 overflow-x-auto rounded-lg bg-muted p-3 text-xs whitespace-pre-wrap">
                      {row.details}
                    </pre>
                  </details>
                ) : null}

                <div className="mt-4 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => void copyReport(row)}>
                    <Copy className="h-4 w-4" />
                    Copy report
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void toggleResolved(row)}>
                    <Check className="h-4 w-4" />
                    {row.resolved_at ? "Reopen" : "Mark resolved"}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
